import './TextArea.css';
import './../input.scss';
import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { t } from '../../../locales/translate';
import { FormField } from '../../../model/form';
import { presence } from '../../../util/util';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { useId } from '../../../util/react-util';

export interface Props extends FormField<string | null> {
  label?: string;
  icon?: IconName;
  actions?: React.ReactNode | React.ReactNodeArray;
  json?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  monospace?: boolean;
}

export function FormTextArea({
  name,
  inputId,
  value,
  label,
  icon,
  actions,
  description,
  placeholder,
  autoFocus,
  required,
  disabled,
  maxLength,
  readOnly,
  className,
  textareaRef,
  errors,
  nullIfEmpty,
  onChange,
  onFocus,
  onBlur,
  touched,
  valid,
  monospace,
}: Props): JSX.Element {
  const [active, setActive] = useState(false);
  const displayErrors = touched && !valid;
  const full = active || placeholder || !!value?.length;

  const inputRef: React.MutableRefObject<HTMLTextAreaElement | null> = useRef(null);
  const id = useId(inputId);

  return (
    <div
      className={classnames(
        'input',
        'textarea',
        { 'textarea--with-icon': icon },
        { 'textarea--disabled': disabled },
        { 'textarea--monospace': monospace },
        className
      )}
    >
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': full,
          'input__container--error': displayErrors,
        })}
      >
        {label && (
          <label className="input__label" htmlFor={id}>
            {label}
            {!required && ` (${t.general.optional})`}
          </label>
        )}
        <div className="input__input-wrapper">
          <div className="textarea__input-wrapper" data-replicated-value={value}>
            <textarea
              id={id}
              name={name}
              className="input__input"
              value={value ?? ''}
              onChange={(v) => {
                if (readOnly) {
                  v.preventDefault();
                  return;
                }
                const newValue = nullIfEmpty ? (presence(v.target.value) ?? null) : v.target.value;
                onChange?.(newValue);
              }}
              onFocus={() => {
                setActive(true);
                onFocus?.();
              }}
              onBlur={() => {
                setActive(false);
                onBlur?.();
              }}
              autoFocus={autoFocus}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              maxLength={maxLength}
              ref={(ref) => {
                inputRef.current = ref;
                if (textareaRef) {
                  textareaRef.current = ref;
                }
              }}
              rows={1}
            />
          </div>
          {actions && <div className="input__actions">{actions}</div>}
          {displayErrors && <Icon name="alert-circle--sm" className="input__alert-circle" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={displayErrors}>
            {errors?.map((error) => {
              return (
                <div className="input__error-message" key={error}>
                  {t.errors[error] || error}
                </div>
              );
            })}
          </Collapse>
        </div>
      </div>
    </div>
  );
}
